import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ICP_MUST_RATING, ICP_RATINGS, ICP_RATING_LABELS, type IcpItem, type IcpRating } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Popover } from '../../../../components/Popover';
import { RatingDots } from '../../../../components/RatingDots';
import { anchorFrom, type AnchorRect } from '../../../../lib/anchored-panel';
import { groupsOf } from '../../icp-view';
import { RATING_HINTS } from './IcpCard';
import styles from '../../../icp/components/icp.module.css';

// DECISIONS-LOG 2026-10-26 "power sorting must keep the table in view": a slim bar right above the table with one chip per profile
// line and its 1–5 number. A chip opens a small pop-over by it with the dots, so a number can be changed while the table is still on
// screen. The numbers go through the same path as the card's dots (use-icp-workspace's `rate`), so nothing about the order differs.

const READ_ONLY_TITLE = 'You can see this job’s ranking but can’t change it.';
const EDITING_TITLE = 'Editing in the panel.';
const FLASH_NOTE = 'Order updated. No new Jev search, no cost.';

interface IcpFocusBarProps {
  /** The lines with the ratings on screen (the server's, with unconfirmed clicks laid over). */
  readonly items: readonly IcpItem[];
  readonly isAtBaseline: boolean;
  readonly canEdit: boolean;
  /** The form is open in the panel: the numbers wait for it. */
  readonly isEditing: boolean;
  readonly flash: boolean;
  readonly onRate: (itemId: string, rating: IcpRating) => void;
  readonly onReset: () => void;
}

interface OpenChip {
  readonly itemId: string;
  readonly anchor: AnchorRect;
}

export function IcpFocusBar({ items, isAtBaseline, canEdit, isEditing, flash, onRate, onReset }: IcpFocusBarProps): ReactNode {
  const [open, setOpen] = useState<OpenChip | null>(null);
  const chips = useRef(new Map<string, HTMLButtonElement>());
  const isLocked = !canEdit || isEditing;
  const lockTitle = !canEdit ? READ_ONLY_TITLE : isEditing ? EDITING_TITLE : undefined;
  const openItem = open && !isLocked ? items.find((item) => item.id === open.itemId) : undefined;

  const toggle = (item: IcpItem, chip: HTMLButtonElement): void => setOpen((current) => (current?.itemId === item.id ? null : { itemId: item.id, anchor: anchorFrom(chip) }));
  const close = (): void => {
    const itemId = open?.itemId;
    setOpen(null);
    if (itemId) chips.current.get(itemId)?.focus();
  };

  return (
    <section className={styles.focusBar} aria-label="Focus on the ideal candidate">
      <span className={styles.focusLabel}>Focus</span>
      <ul className={styles.focusChips}>
        {groupsOf(items).flatMap((entry) => entry.items).map((item) => (
          <li key={item.id}>
            <button
              ref={(element) => {
                if (element) chips.current.set(item.id, element);
                else chips.current.delete(item.id);
              }}
              type="button"
              className={styles.focusChip}
              data-must={item.rating === ICP_MUST_RATING || undefined}
              data-open={openItem?.id === item.id || undefined}
              // A click on another chip while one is open is not a click away: the pop-over leaves clicks inside `[data-popover]` alone,
              // so the chip decides — the same chip closes it, another one moves it.
              data-popover
              aria-haspopup="dialog"
              aria-expanded={openItem?.id === item.id}
              disabled={isLocked}
              title={lockTitle ?? `${item.text}: ${ICP_RATING_LABELS[item.rating]}`}
              onClick={(event) => toggle(item, event.currentTarget)}
            >
              <span className={styles.focusChipName}>{item.text}</span>
              <span className={styles.focusChipNumber}>{item.rating}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className={styles.focusEnd}>
        <Button size="sm" variant="ghost" disabled={!canEdit || isAtBaseline || isEditing} title={lockTitle} onClick={onReset}>
          Reset focus
        </Button>
        <span role="status" className={flash ? styles.flash : undefined}>
          {flash ? FLASH_NOTE : ''}
        </span>
      </div>
      <Popover anchor={openItem ? (open?.anchor ?? null) : null} onClose={close} label={openItem ? `Focus for ${openItem.text}` : 'Focus'} align="left">
        {openItem && <FocusPicker key={openItem.id} item={openItem} onRate={onRate} />}
      </Popover>
    </section>
  );
}

function FocusPicker({ item, onRate }: { readonly item: IcpItem; readonly onRate: (itemId: string, rating: IcpRating) => void }): ReactNode {
  const body = useRef<HTMLDivElement | null>(null);
  // The pop-over is measured hidden for a frame first, and a hidden control cannot take focus.
  useEffect(() => {
    const frame = requestAnimationFrame(() => body.current?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]')?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div ref={body} className={styles.focusPicker}>
      <p className={styles.focusPickerTitle}>{item.text}</p>
      <div className={styles.focusPickerRow}>
        <RatingDots
          label={`Focus for ${item.text}`}
          value={item.rating}
          hints={RATING_HINTS}
          onChange={(value) => {
            const rating = ICP_RATINGS.find((entry) => entry === value);
            if (rating) onRate(item.id, rating);
          }}
        />
        <span className={styles.focusPickerLabel}>{ICP_RATING_LABELS[item.rating]}</span>
      </div>
    </div>
  );
}
